import { Component, Input, OnInit } from '@angular/core';
import { MailService } from '../../services/mail.service';
import { TrimWhitespacesPipe } from '../../pipes/trim-whitespaces.pipe';
import { WorkContact, WorkContactsService, WorkProject } from '../../_generated/work-rest-api';

/** Eine Adresse in der Form, in der die Karte sie anzeigt und an die Karten-App übergibt. */
interface ProjectAddress {
  street: string;
  zipcode: string;
  city: string;
  canton: string;
}

/**
 * Kundendaten eines Projekts als aufklappbarer Bereich: Adresse plus Telefonnummern und
 * E-Mail-Adressen zum direkten Anrufen bzw. Schreiben.
 *
 * Der Kontakt am Projekt (`project.container.contact`) enthält Adresse und Firma, aber **keine**
 * Telefonnummern und Mails — die Projekt-API lädt diese Relationen nicht mit. Deshalb holt die
 * Komponente den Kontakt beim Aufklappen selbst nachladen, über denselben Endpoint wie die
 * Kontaktdetailseite. Fehlt die Berechtigung dafür, bleibt die Adresse aus dem Projekt sichtbar.
 *
 * Weicht die Objektadresse des Projekts von der Kundenadresse ab, wird sie zusätzlich mit einem
 * Hinweis angezeigt — auf dem Bau ist genau das die relevante Adresse.
 */
@Component({
  selector: 'ax-project-customer',
  templateUrl: './ax-project-customer.component.html',
})
export class AxProjectCustomerComponent implements OnInit {
  @Input() project: WorkProject;
  public accordionState = false;
  /** Nur gesetzt, wenn die Objektadresse von der Kundenadresse abweicht. */
  public differingObjectAddress: ProjectAddress | null = null;
  /** Nachgeladener Kontakt mit Telefonnummern und Mails; bis dahin gilt der Kontakt am Projekt. */
  public contactWithDetails: WorkContact | undefined;
  public isLoading = false;

  constructor(
    private readonly axContactsService: WorkContactsService,
    private readonly mailService: MailService,
    private readonly trimWhitespacesPipe: TrimWhitespacesPipe,
  ) {
  }

  public ngOnInit(): void {
    // Einmalig auswerten: das Projekt wechselt auf dieser Karte nicht mehr.
    this.differingObjectAddress = AxProjectCustomerComponent.RESOLVE_DIFFERING_OBJECT_ADDRESS(this.project);
  }

  public get contact(): WorkContact | undefined {
    return this.contactWithDetails || this.project?.container?.contact;
  }

  public toggleAccordion(): void {
    this.accordionState = !this.accordionState;
    if (this.accordionState) {
      this.loadContactDetails();
    }
  }

  /** Adresse in der Karten-App öffnen — gleiche Mechanik wie auf der Kontaktdetailseite. */
  public openGoogleMaps(address?: ProjectAddress | null): void {
    const target = address ?? AxProjectCustomerComponent.TO_ADDRESS(this.contact);
    if (!target) {
      return;
    }
    const addressString = encodeURI(`${target.street} ,${target.zipcode} ${target.city}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${addressString}`, '_blank');
  }

  public call(phoneNumber: string): void {
    window.open(`tel:${this.trimWhitespacesPipe.transform(phoneNumber)}`, '_blank');
  }

  public createMail(email: string): void {
    this.mailService.createMail(email, 'Anfrage aus Axova-App')
      .then()
      .catch(() => {
        window.open(`mailto:${email}?subject=Anfrage aus Axova-App`, '_blank');
      });
  }

  /**
   * Objektadresse, falls sie von der Kundenadresse abweicht — sonst NULL.
   *
   * Verglichen wird normalisiert (Kleinschreibung, Whitespace zusammengezogen): in den Daten
   * unterscheiden sich die beiden Adressen oft nur in Schreibweise und Leerzeichen, und dafür
   * braucht es keinen Hinweis. Ohne gesetzte Objektstrasse gilt ohnehin die Kundenadresse.
   */
  private static RESOLVE_DIFFERING_OBJECT_ADDRESS(project: WorkProject): ProjectAddress | null {
    const objectAddress: ProjectAddress = {
      street: project?.objectStreet || '',
      zipcode: project?.objectZip || '',
      city: project?.objectCity || '',
      canton: project?.objectCanton || '',
    };
    if (!objectAddress.street) {
      return null;
    }
    const contactAddress = AxProjectCustomerComponent.TO_ADDRESS(project?.container?.contact);
    const isSameAddress =
      !!contactAddress &&
      AxProjectCustomerComponent.NORMALIZE(objectAddress) === AxProjectCustomerComponent.NORMALIZE(contactAddress);
    return isSameAddress ? null : objectAddress;
  }

  private static TO_ADDRESS(contact: WorkContact | undefined): ProjectAddress | null {
    if (!contact) {
      return null;
    }
    return {
      street: contact.street || '',
      zipcode: contact.zipcode || '',
      city: contact.city || '',
      canton: contact.canton || '',
    };
  }

  private static NORMALIZE(address: ProjectAddress): string {
    return `${address.street} ${address.zipcode} ${address.city}`.replace(/\s+/g, ' ').trim().toLowerCase();
  }

  private loadContactDetails(): void {
    const contactId = this.project?.container?.contactId;
    if (this.contactWithDetails || this.isLoading || !contactId) {
      return;
    }
    this.isLoading = true;
    // Der v1-Endpoint erwartet die Id als String (Path-Parameter im generierten Client).
    this.axContactsService.contactsV1ControllerFindOne({ id: `${contactId}` }).subscribe({
      next: contact => {
        this.contactWithDetails = contact;
        this.isLoading = false;
      },
      error: () => {
        // Ohne Nachladen bleibt die Adresse aus dem Projekt — kein Fehlerdialog, das wäre hier Lärm.
        this.isLoading = false;
      },
    });
  }
}
